import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Apple, Smartphone, Globe, Copy, Check, X, ArrowUpRight, ExternalLink, Info } from 'lucide-react';
import { type MouseEvent as ReactMouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import '../css/qnbg-download.css'
import appicon from './appicon1.jpg'
import shot1 from './qnbg_1.jpg'
import shot2 from './qnbg_2.jpg'
import shot3 from './qnbg_3.jpg'
import shot4 from './qnbg_4.jpg'

const IOS_URL = 'https://apps.apple.com/cn/app/id6755644246';
const ANDROID_CN_URL = 'https://file.market.xiaomi.com/download/AppStore/00a01ed9467164eaeaa2cfcc0471fc1245dc3d6ec/com.yelizibg_1.3.2.apk';
const ANDROID_GLOBAL_URL = 'https://play.google.com/store/apps/details?id=com.yelizibg';
const WEB_URL = 'http://qnbg-web.suntable.cn/home';

/** 跳转被拦截后，多久判定为「没有跳转」（毫秒） */
const REDIRECT_CHECK_DELAY = 2500;

/** 识别会拦截下载/外链跳转的内置浏览器环境 */
function detectBlockedEnv(): 'wechat' | 'inapp' | 'normal' {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('micromessenger') || ua.includes('weixin') || ua.includes('wechat')) {
        return 'wechat';
    }
    if (
        ua.includes('weibo') ||
        ua.includes('qq/') ||
        ua.includes(' qq') ||
        ua.includes('dingtalk') ||
        ua.includes('alipayclient') ||
        ua.includes('baiduboxapp') ||
        ua.includes('quark') ||
        ua.includes('douban') ||
        ua.includes('douyin') ||
        ua.includes('toutiao')
    ) {
        return 'inapp';
    }
    return 'normal';
}

const translations = [
    {
        appName: '表格工坊',
        tagline: '快速表格制作工具',
        intro: '一款去繁就简、专注实用的手机表格制作工具。不用学习复杂的 Excel 操作，也不用记住繁琐的公式。打开表格工坊，随时随地创建、编辑和管理表格，让制作表格变得简单又高效。',
        f1Title: '简洁无负担',
        f1Desc: '去除复杂且不常用的功能，保留真正高频、实用的表格功能。界面清爽、操作直观，无需专门学习，打开就能开始制作。',
        f2Title: '功能简单，但足够强大',
        f2Desc: '支持一键美化、智能计算、查找替换、合并单元格、图片插入、冻结行列、表格分享，以及丰富的表格模板。无论是工作汇报、数据统计、财务记录，还是日常经营，都能轻松应对。',
        f3Title: '适合日常经营',
        f3Desc: '特别适合个体经营者和小型商户记录日常业务：',
        f3Tags: ['进销存管理', '库存记录', '采购记录', '销售记录', '订单管理', '报价单', '出货单', '收支记录', '客户信息', '商品信息'],
        f3Extra: '也可以用于个人记账、工作记录、数据整理、信息收集等各种需要表格的场景。',
        f4Title: '真正简单易用',
        f4Desc: '不需要教程，不需要记复杂公式。从创建第一张表开始，几步就能完成一份清晰、整洁的专业表格。无论你是职场新人、个体店主，还是经常需要处理数据的普通用户，表格工坊都希望让表格变得更简单。',
        screenshotTitle: '应用预览',
        downloadNow: '立即下载',
        iosBtn: 'App Store 下载',
        iosSub: 'iPhone · iPad',
        androidBtn: '安卓下载',
        androidSub: 'Android',
        webBtn: '网页版',
        webSub: '在线使用',
        home: '首页',
        privacy: '隐私政策',
        term: '使用条款',
        member: '会员协议',
        copyright: 'Copyright © 2024 野离子网络科技. All rights reserved.',
        icp: '粤ICP备2021177159号-2',
        footerTip: '表格工坊由广州野离子网络科技有限公司研发',
        wxTip: '当前在微信中打开，下载链接可能被限制',
        guideWxTitle: '请点击右上角，在浏览器中打开',
        guideWxDesc: '微信内置浏览器不支持直接下载安装，请按以下步骤操作：',
        guideWxStep1: '点击右上角的「···」按钮',
        guideWxStep2: '选择「在浏览器打开」',
        guideWxStep3: '在浏览器中再次点击下载即可',
        guideWxHint: '也可以复制链接后，粘贴到系统浏览器（Safari / Chrome）中打开',
        guideFallbackTitle: '下载没有自动开始？',
        guideFallbackDesc: '当前浏览器可能限制了跳转或下载，建议复制链接后在系统浏览器（Safari / Chrome）中打开。',
        guideFallbackHint: '如果已经开始下载，直接关闭本提示即可。',
        guideInappTitle: '请在浏览器中打开',
        guideInappDesc: '当前应用内置浏览器可能拦截了下载，请点击右上角菜单选择「用浏览器打开」，或复制链接后到浏览器中下载。',
        copyLink: '复制链接地址',
        copied: '已复制',
        copyFail: '复制失败，请长按链接手动复制',
        retryOpen: '继续尝试打开',
        close: '关闭',
    },
    {
        appName: '表格工坊',
        tagline: '快速表格製作工具',
        intro: '一款去繁就簡、專注實用的手機表格製作工具。不用學習複雜的 Excel 操作，也不用記住繁瑣的公式。打開表格工坊，隨時隨地建立、編輯和管理表格，讓製作表格變得簡單又高效。',
        f1Title: '簡潔無負擔',
        f1Desc: '去除複雜且不常用的功能，保留真正高頻、實用的表格功能。介面清爽、操作直觀，無需專門學習，打開就能開始製作。',
        f2Title: '功能簡單，但足夠強大',
        f2Desc: '支援一鍵美化、智慧計算、尋找取代、合併儲存格、圖片插入、凍結行列、表格分享，以及豐富的表格範本。無論是工作彙報、資料統計、財務記錄，還是日常經營，都能輕鬆應對。',
        f3Title: '適合日常經營',
        f3Desc: '特別適合個體經營者和小型商戶記錄日常業務：',
        f3Tags: ['進銷存管理', '庫存記錄', '採購記錄', '銷售記錄', '訂單管理', '報價單', '出貨單', '收支記錄', '客戶資訊', '商品資訊'],
        f3Extra: '也可以用於個人記帳、工作記錄、資料整理、資訊收集等各種需要表格的場景。',
        f4Title: '真正簡單易用',
        f4Desc: '不需要教學，不需要記複雜公式。從建立第一張表開始，幾步就能完成一份清晰、整潔的專業表格。無論你是職場新人、個體店主，還是經常需要處理資料的普通用戶，表格工坊都希望讓表格變得更簡單。',
        screenshotTitle: '應用預覽',
        downloadNow: '立即下載',
        iosBtn: 'App Store 下載',
        iosSub: 'iPhone · iPad',
        androidBtn: 'Android 下載',
        androidSub: 'Android',
        webBtn: '網頁版',
        webSub: '線上使用',
        home: '首頁',
        privacy: '隱私權政策',
        term: '使用條款',
        member: '會員協議',
        copyright: 'Copyright © 2024 野離子網路科技. All rights reserved.',
        icp: '粵ICP備2021177159號-2',
        footerTip: '表格工坊由廣州野離子網路科技有限公司研發',
        wxTip: '當前在微信中打開，下載連結可能被限制',
        guideWxTitle: '請點擊右上角，在瀏覽器中打開',
        guideWxDesc: '微信內建瀏覽器不支援直接下載安裝，請按以下步驟操作：',
        guideWxStep1: '點擊右上角的「···」按鈕',
        guideWxStep2: '選擇「在瀏覽器打開」',
        guideWxStep3: '在瀏覽器中再次點擊下載即可',
        guideWxHint: '也可以複製連結後，粘貼到系統瀏覽器（Safari / Chrome）中打開',
        guideFallbackTitle: '下載沒有自動開始？',
        guideFallbackDesc: '當前瀏覽器可能限制了跳轉或下載，建議複製連結後在系統瀏覽器（Safari / Chrome）中打開。',
        guideFallbackHint: '如果已經開始下載，直接關閉本提示即可。',
        guideInappTitle: '請在瀏覽器中打開',
        guideInappDesc: '當前應用內建瀏覽器可能攔截了下載，請點擊右上角選單選擇「用瀏覽器打開」，或複製連結後到瀏覽器中下載。',
        copyLink: '複製連結位址',
        copied: '已複製',
        copyFail: '複製失敗，請長按連結手動複製',
        retryOpen: '繼續嘗試打開',
        close: '關閉',
    },
    {
        appName: 'Table Workshop',
        tagline: 'Quick Table Maker',
        intro: 'A simple and focused mobile table-making tool. No need to master complex Excel operations or memorize tedious formulas. Open Table Workshop and create, edit, and manage tables anytime, anywhere — making tables simple and efficient.',
        f1Title: 'Clean & Simple',
        f1Desc: 'We removed complex and rarely-used features, keeping only the truly practical, high-frequency table tools. A clean interface and intuitive interactions — no learning required, start creating right away.',
        f2Title: 'Simple, yet Powerful',
        f2Desc: 'One-tap beautification, smart calculation, find & replace, merge cells, image insertion, freeze rows & columns, table sharing, and a rich set of templates. Whether for work reports, data statistics, financial records, or daily operations, it handles everything with ease.',
        f3Title: 'Built for Daily Business',
        f3Desc: 'Perfect for individual sellers and small merchants to record daily business:',
        f3Tags: ['Inventory Management', 'Stock Records', 'Purchase Records', 'Sales Records', 'Order Management', 'Quotes', 'Delivery Notes', 'Income & Expense', 'Customer Info', 'Product Info'],
        f3Extra: 'Also great for personal budgeting, work records, data organization, information collection, and any other table-based scenario.',
        f4Title: 'Truly Easy to Use',
        f4Desc: 'No tutorials needed, no complex formulas to remember. From your very first table, you can complete a clear and professional spreadsheet in just a few steps. Whether you are new to the workplace, a shop owner, or someone who works with data regularly, Table Workshop is here to make tables simpler.',
        screenshotTitle: 'Preview',
        downloadNow: 'Download Now',
        iosBtn: 'Download on the App Store',
        iosSub: 'iPhone · iPad',
        androidBtn: 'Android Download',
        androidSub: 'Android',
        webBtn: 'Web Version',
        webSub: 'Use Online',
        home: 'Home',
        privacy: 'Privacy Policy',
        term: 'Terms of Use',
        member: 'Membership',
        copyright: 'Copyright © 2024 Yelizi Network Technology. All rights reserved.',
        icp: '',
        footerTip: 'Developed by Guangzhou Yelizi Network Technology Co., Ltd.',
        wxTip: 'Opened in WeChat — download links may be blocked',
        guideWxTitle: 'Tap the top-right menu to open in a browser',
        guideWxDesc: 'The WeChat in-app browser does not support direct downloads. Please follow these steps:',
        guideWxStep1: 'Tap the "···" button in the top-right corner',
        guideWxStep2: 'Choose "Open in Browser"',
        guideWxStep3: 'Tap download again in the browser',
        guideWxHint: 'You can also copy the link and paste it into Safari or Chrome',
        guideFallbackTitle: 'Download did not start?',
        guideFallbackDesc: 'Your browser may have blocked the redirect or download. Copy the link and open it in your system browser (Safari / Chrome).',
        guideFallbackHint: 'If the download already started, just close this message.',
        guideInappTitle: 'Please open in a browser',
        guideInappDesc: 'This in-app browser may have blocked the download. Open the top-right menu and choose "Open in Browser", or copy the link and download it in your browser.',
        copyLink: 'Copy link',
        copied: 'Copied',
        copyFail: 'Copy failed, please long-press the link to copy manually',
        retryOpen: 'Try again',
        close: 'Close',
    }
];

function Download() {
    const [searchParams] = useSearchParams();
    const theme = searchParams.get('theme');
    const language = searchParams.get('language') || '英文';

    const newTheme = theme === 'dark' ? 'dark' : 'light';
    document.body.className = newTheme;

    const t = language === '简体' ? translations[0] : (language === '繁体' ? translations[1] : translations[2]);
    const androidUrl = language === '简体' ? ANDROID_CN_URL : ANDROID_GLOBAL_URL;
    const query = `?language=${language}&theme=${theme}`;

    /** 当前所处浏览器环境：微信 / 其它 App 内置浏览器 / 普通浏览器 */
    const env = useMemo(() => detectBlockedEnv(), []);

    /** 引导弹层：wechat=微信蒙层引导，inapp=其它内置浏览器，fallback=普通浏览器未跳转 */
    const [guide, setGuide] = useState<{ type: 'wechat' | 'inapp' | 'fallback'; url: string } | null>(null);
    const [copyState, setCopyState] = useState<'idle' | 'ok' | 'fail'>('idle');
    const timerRef = useRef<number | null>(null);

    useEffect(() => () => {
        if (timerRef.current) window.clearTimeout(timerRef.current);
    }, []);

    /* 弹层打开时锁定页面滚动，并支持 ESC 关闭 */
    useEffect(() => {
        if (!guide) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setGuide(null);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [guide]);

    /* 复制链接（微信等环境下 Clipboard API 不可用，降级 execCommand） */
    const copyLink = async (url: string) => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(url);
            } else {
                const input = document.createElement('textarea');
                input.value = url;
                input.setAttribute('readonly', '');
                input.style.position = 'fixed';
                input.style.top = '-1000px';
                input.style.opacity = '0';
                document.body.appendChild(input);
                input.select();
                input.setSelectionRange(0, url.length);
                const ok = document.execCommand('copy');
                document.body.removeChild(input);
                if (!ok) throw new Error('copy failed');
            }
            setCopyState('ok');
        } catch (e) {
            setCopyState('fail');
        }
        window.setTimeout(() => setCopyState('idle'), 2500);
    };

    /* 点击下载：微信/内置浏览器直接引导；普通浏览器先跳转，未跳转再引导 */
    const handleDownload = (e: ReactMouseEvent<HTMLAnchorElement>, url: string) => {
        if (env === 'wechat' || env === 'inapp') {
            e.preventDefault();
            setGuide({ type: env, url });
            return;
        }
        e.preventDefault();
        // 网页版只是打开站点，不需要「下载未开始」兜底引导
        if (url === WEB_URL) {
            const w = window.open(url, '_blank');
            if (!w) window.location.href = url;
            return;
        }
        const newWindow = window.open(url, '_blank');
        if (!newWindow) {
            // 弹出被拦截：直接在当前页跳转；若仍停留在当前页，再提示引导
            window.location.href = url;
            if (timerRef.current) window.clearTimeout(timerRef.current);
            timerRef.current = window.setTimeout(() => {
                // 页面仍处于前台（未隐藏且仍有焦点）说明跳转/下载没有发生
                if (!document.hidden && document.hasFocus()) {
                    setGuide({ type: 'fallback', url });
                }
            }, REDIRECT_CHECK_DELAY);
        }
    };

    const isWechatGuide = guide?.type === 'wechat';

    return (
        <div className="qbg-dl">
            <Helmet>
                <title>{t.appName} - {t.tagline}</title>
                <meta property="og:title" content={`${t.appName} - ${t.tagline}`} />
                <meta property="og:description" content={t.intro} />
                <meta property="og:type" content="website" />
                <meta property="og:image" content={`${window.location.origin}/appicon1.jpg`} />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={`${t.appName} - ${t.tagline}`} />
                <meta name="twitter:description" content={t.intro} />
                <meta name="twitter:image" content={`${window.location.origin}/appicon1.jpg`} />
            </Helmet>

            {/* Hero */}
            <header className="qbg-dl-hero">
                <img className="qbg-dl-icon" src={appicon} alt={t.appName} />
                <h1 className="qbg-dl-name">{t.appName}</h1>
                <p className="qbg-dl-tagline">{t.tagline}</p>
                <p className="qbg-dl-intro">{t.intro}</p>
                <div className="qbg-dl-buttons">
                    <a
                        className="qbg-dl-btn qbg-dl-btn-ios"
                        href={IOS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => handleDownload(e, IOS_URL)}
                    >
                        <Apple size={22} />
                        <span>
                            {t.iosBtn}
                            <em className="qbg-dl-btn-sub">{t.iosSub}</em>
                        </span>
                    </a>
                    <a
                        className="qbg-dl-btn qbg-dl-btn-android"
                        href={androidUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => handleDownload(e, androidUrl)}
                    >
                        <Smartphone size={22} />
                        <span>
                            {t.androidBtn}
                            <em className="qbg-dl-btn-sub">{t.androidSub}</em>
                        </span>
                    </a>
                    <a
                        className="qbg-dl-btn qbg-dl-btn-web"
                        href={WEB_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => handleDownload(e, WEB_URL)}
                    >
                        <Globe size={22} />
                        <span>
                            {t.webBtn}
                            <em className="qbg-dl-btn-sub">{t.webSub}</em>
                        </span>
                    </a>
                </div>
                {env === 'wechat' && (
                    <p className="qbg-dl-wxtip">
                        <Info size={14} />
                        {t.wxTip}
                    </p>
                )}
            </header>

            {/* 内容 */}
            <main className="qbg-dl-body">
                <section className="qbg-dl-section">
                    <div className="qbg-dl-feature">
                        <h3>{t.f1Title}</h3>
                        <p>{t.f1Desc}</p>
                    </div>
                    <div className="qbg-dl-feature">
                        <h3>{t.f2Title}</h3>
                        <p>{t.f2Desc}</p>
                    </div>
                    <div className="qbg-dl-feature">
                        <h3>{t.f3Title}</h3>
                        <p>{t.f3Desc}</p>
                        <div className="qbg-dl-tags">
                            {t.f3Tags.map((tag) => (
                                <span key={tag} className="qbg-dl-tag">{tag}</span>
                            ))}
                        </div>
                        <p style={{ marginTop: 12 }}>{t.f3Extra}</p>
                    </div>
                    <div className="qbg-dl-feature">
                        <h3>{t.f4Title}</h3>
                        <p>{t.f4Desc}</p>
                    </div>
                </section>

                <section className="qbg-dl-section">
                    <h2 className="qbg-dl-title">{t.screenshotTitle}</h2>
                    <div className="qbg-dl-shots">
                        <div className="qbg-dl-shot"><img src={shot1} alt={`${t.appName} 1`} /></div>
                        <div className="qbg-dl-shot"><img src={shot2} alt={`${t.appName} 2`} /></div>
                        <div className="qbg-dl-shot"><img src={shot3} alt={`${t.appName} 3`} /></div>
                        <div className="qbg-dl-shot"><img src={shot4} alt={`${t.appName} 4`} /></div>
                    </div>
                </section>
            </main>

            {/* 底部 */}
            <footer className="qbg-dl-footer">
                <div className="qbg-dl-links">
                    <Link to={`/qnbg${query}`}>{t.home}</Link>
                    <Link to={`/qnbg/privacyios${query}`}>{t.privacy}</Link>
                    <Link to={`/qnbg/term${query}`}>{t.term}</Link>
                    <Link to={`/qnbg/member${query}`}>{t.member}</Link>
                </div>
                <p className="qbg-dl-copy">{t.footerTip}</p>
                {t.icp && (
                    <a className="qbg-dl-icp" href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">{t.icp}</a>
                )}
                <p className="qbg-dl-copy">{t.copyright}</p>
            </footer>

            {/* 微信内置浏览器：右上角引导蒙层 */}
            {isWechatGuide && guide && (
                <div className="qbg-guide qbg-guide-wechat" onClick={() => setGuide(null)}>
                    <div className="qbg-guide-arrow">
                        <ArrowUpRight size={40} strokeWidth={2.5} />
                        <span>{t.guideWxStep1}</span>
                    </div>
                    <div className="qbg-guide-card" onClick={(e) => e.stopPropagation()}>
                        <h3 className="qbg-guide-title">{t.guideWxTitle}</h3>
                        <p className="qbg-guide-desc">{t.guideWxDesc}</p>
                        <ol className="qbg-guide-steps">
                            <li>{t.guideWxStep1}</li>
                            <li>{t.guideWxStep2}</li>
                            <li>{t.guideWxStep3}</li>
                        </ol>
                        <input
                            className="qbg-guide-url"
                            readOnly
                            value={guide.url}
                            onFocus={(e) => e.currentTarget.select()}
                            onClick={(e) => e.currentTarget.select()}
                        />
                        <button className="qbg-guide-btn" onClick={() => copyLink(guide.url)}>
                            {copyState === 'ok' ? <Check size={18} /> : <Copy size={18} />}
                            {copyState === 'ok' ? t.copied : t.copyLink}
                        </button>
                        <p className="qbg-guide-hint">
                            {copyState === 'fail' ? t.copyFail : t.guideWxHint}
                        </p>
                    </div>
                </div>
            )}

            {/* 其它 App 内置浏览器 / 普通浏览器未跳转：底部弹窗引导 */}
            {guide && guide.type !== 'wechat' && (
                <div className="qbg-guide qbg-guide-sheet" onClick={() => setGuide(null)}>
                    <div className="qbg-guide-panel" onClick={(e) => e.stopPropagation()}>
                        <span className="qbg-guide-bar" />
                        <button className="qbg-guide-close" onClick={() => setGuide(null)} aria-label={t.close}>
                            <X size={20} />
                        </button>
                        <h3 className="qbg-guide-title">
                            {guide.type === 'inapp' ? t.guideInappTitle : t.guideFallbackTitle}
                        </h3>
                        <p className="qbg-guide-desc">
                            {guide.type === 'inapp' ? t.guideInappDesc : t.guideFallbackDesc}
                        </p>
                        <input
                            className="qbg-guide-url"
                            readOnly
                            value={guide.url}
                            onFocus={(e) => e.currentTarget.select()}
                            onClick={(e) => e.currentTarget.select()}
                        />
                        <div className="qbg-guide-actions">
                            <button className="qbg-guide-btn" onClick={() => copyLink(guide.url)}>
                                {copyState === 'ok' ? <Check size={18} /> : <Copy size={18} />}
                                {copyState === 'ok' ? t.copied : t.copyLink}
                            </button>
                            <a
                                className="qbg-guide-btn qbg-guide-btn-ghost"
                                href={guide.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setGuide(null)}
                            >
                                <ExternalLink size={18} />
                                {t.retryOpen}
                            </a>
                        </div>
                        <p className="qbg-guide-hint">
                            {copyState === 'fail' ? t.copyFail : t.guideFallbackHint}
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}

export default Download;
